import { GripVerticalIcon, Trash2Icon } from 'lucide-react';
import { useMemo } from 'react';

import {
  ConditionOperator,
  PropertyConditionName,
  WorkflowScopeType,
} from '@invenco/common-domain/enums';

import { Button } from '@/components/core/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/core/select';
import { cn } from '@/shared/helpers';

import { EditorConditionLeaf } from '../editor-model';
import {
  BufferedFieldInput,
  flushClassName,
  isValuePresent,
  SingleSelectCombobox,
  type FieldInputValue,
  type Option,
} from '../field-input';
import {
  getOperatorLabel,
  getPropertyLabel,
  getPropertyOperators,
  getScopeProperties,
  resolveSubPropertyInput,
  resolveValueInput,
} from '../field-registry';
import {
  setLeafOperator,
  setLeafProperty,
  setLeafSubProperty,
  setLeafValue,
  type UpdateRule,
} from './condition-draft';

export interface ConditionLeafRowProps {
  leaf: EditorConditionLeaf;
  scopeType: WorkflowScopeType;
  /** True when the validator flags this leaf as incomplete. */
  incomplete: boolean;
  /** Stages this row's edits; the row applies the condition-draft recipes itself. */
  updateRule: UpdateRule;
  onDelete: () => void;
  /** Sortable wiring; absent on the unstaged empty-state row, whose handle is inert. */
  rowRef?: (element: Element | null) => void;
  handleRef?: (element: Element | null) => void;
}

/** One editable condition leaf: drag-handle gutter, field card, hover-revealed delete. */
export function ConditionLeafRow({
  leaf,
  scopeType,
  incomplete,
  updateRule,
  onDelete,
  rowRef,
  handleRef,
}: ConditionLeafRowProps) {
  return (
    <div ref={rowRef} className="group/row relative">
      <div className="flex rounded-xl bg-secondary p-2">
        <button
          type="button"
          ref={handleRef}
          aria-label="Reorder condition"
          disabled={!handleRef}
          // pr-2 only: the icon's own inset optically balances the gutter.
          className="flex cursor-grab items-center pr-2 text-muted-foreground outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:ring-inset disabled:pointer-events-none"
        >
          <GripVerticalIcon className="size-3.5" />
        </button>
        <div className="min-w-0 flex-1 overflow-hidden rounded-lg border border-input bg-card">
          <ConditionLeafInputs
            leaf={leaf}
            scopeType={scopeType}
            incomplete={incomplete}
            updateRule={updateRule}
          />
        </div>
      </div>
      <Button
        type="button"
        variant="outline"
        size="icon-xs"
        aria-label="Delete condition"
        // Centred via margin, not translate — the pressed state's translate-y-px
        // would replace translate-based centring and drop the button mid-click.
        className="absolute top-1/2 -right-1 -mt-3 text-muted-foreground opacity-0 transition-opacity group-focus-within/row:opacity-100 group-hover/row:opacity-100 hover:text-destructive focus-visible:opacity-100"
        onClick={onDelete}
      >
        <Trash2Icon className="size-3.5" />
      </Button>
    </div>
  );
}

interface ConditionLeafInputsProps {
  leaf: EditorConditionLeaf;
  scopeType: WorkflowScopeType;
  incomplete: boolean;
  updateRule: UpdateRule;
}

function ConditionLeafInputs({
  leaf,
  scopeType,
  incomplete,
  updateRule,
}: ConditionLeafInputsProps) {
  const operators = getPropertyOperators(leaf.property);
  const valueDescriptor = resolveValueInput(leaf.property, leaf.operator);
  const subPropertyDescriptor = resolveSubPropertyInput(leaf.property);
  const hasValue = valueDescriptor.kind !== 'none';

  const showIncomplete = incomplete && !leaf.fresh;

  const propertyOptions = useMemo<Option[]>(
    () =>
      getScopeProperties(scopeType).map((property) => ({
        value: property,
        label: getPropertyLabel(property),
      })),
    [scopeType],
  );

  const operatorSelect = (
    <Select
      value={leaf.operator}
      onValueChange={(next) =>
        updateRule((r) => setLeafOperator(r, leaf.id, next as ConditionOperator))
      }
    >
      <SelectTrigger className={cn('w-full', flushClassName)} aria-label="Operator">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {operators.map((operator) => (
          <SelectItem key={operator} value={operator}>
            {getOperatorLabel(operator)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    // The bordered card is the frame's — the inputs draw only their internal dividers.
    <div className="[&>*+*]:border-t [&>*+*]:border-input">
      <SingleSelectCombobox
        aria-label="Property"
        options={propertyOptions}
        value={leaf.property}
        placeholder="Property"
        emptyText="No properties"
        // A leaf always targets a property, so there's nothing to clear to.
        showClear={false}
        className={cn('w-full', flushClassName)}
        onChange={(next) => {
          if (typeof next === 'string') {
            updateRule((r) => setLeafProperty(r, leaf.id, next as PropertyConditionName));
          }
        }}
      />

      {subPropertyDescriptor ? (
        // Wrapped so the row divider sits on the cell, not the input, whose
        // focus border would repaint the shared edge.
        <div className="min-w-0">
          <BufferedFieldInput
            className={flushClassName}
            aria-label="Field"
            aria-invalid={showIncomplete && !isValuePresent(leaf.subProperty) ? true : undefined}
            descriptor={subPropertyDescriptor}
            value={leaf.subProperty}
            placeholder="Field"
            onChange={(next) =>
              updateRule((r) =>
                setLeafSubProperty(r, leaf.id, typeof next === 'string' ? next : ''),
              )
            }
          />
        </div>
      ) : null}

      {hasValue ? (
        <div className="grid grid-cols-[10rem_1fr]">
          {operatorSelect}
          <div className="min-w-0 border-l border-input">
            <BufferedFieldInput
              className={flushClassName}
              aria-label="Value"
              aria-invalid={showIncomplete && !isValuePresent(leaf.value) ? true : undefined}
              descriptor={valueDescriptor}
              value={leaf.value as FieldInputValue}
              placeholder="Value"
              onChange={(next) => updateRule((r) => setLeafValue(r, leaf.id, next))}
            />
          </div>
        </div>
      ) : (
        operatorSelect
      )}
    </div>
  );
}
